<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import {
  ContentsContext,
  Feature,
  PageName,
  SummaryInfo,
} from '@/api/tracking';
import { Tooltip } from 'floating-vue';
import AdpDocumentCardSmall from '@/components/common/article/adp-document-card-small.vue';
import CitationPill from '@/components/common/atoms/citation-pill.vue';
import { useTooltipOverlayGuard } from '@/components/home/insights/use-tooltip-overlay-guard';
import { AdpDocument } from '@/types';
import type { InsightSourceContext } from '@/types/insights';

interface Props {
  position: number;
  adpDocument: AdpDocument;
  pageName: PageName;
  feature: Feature;
  // insight からの閲覧計測用。他機能(survey/market sizing)では渡さない
  insightSourceContext?: InsightSourceContext;
  // 要約起点の閲覧計測に載せる属性。survey で渡す
  contentsContext?: ContentsContext;
  summaryInfo?: SummaryInfo;
  variant?: 'white' | 'gray';
}

withDefaults(defineProps<Props>(), { variant: 'white' });

// VTooltipがグローバル定義で型推論が効かないので、型引数を与える
const tooltip = useTemplateRef<InstanceType<typeof Tooltip>>('tooltip');

// CitationPill はコンポーネントなので、overlay guard が hover 判定に使う DOM 要素はルートの $el から取る
const triggerPill = useTemplateRef('triggerPill');
const trigger = computed(
  () => (triggerPill.value?.$el as HTMLElement | undefined) ?? null,
);

const popperContent = useTemplateRef('popperContent');

const { onHide } = useTooltipOverlayGuard(tooltip, trigger, popperContent);
</script>

<template>
  <VTooltip ref="tooltip" theme="popover" class="source-badge" @hide="onHide">
    <CitationPill
      ref="triggerPill"
      :doc-type="adpDocument.doc_type"
      :number="position"
      :variant="variant"
    />
    <template #popper>
      <div ref="popperContent">
        <AdpDocumentCardSmall
          :adp-document="adpDocument"
          :page-name="pageName"
          :feature="feature"
          :insight-source-context="insightSourceContext"
          :contents-context="contentsContext"
          :summary-info="summaryInfo"
          show-published-date
          show-option-menu
          class="document-card"
        />
      </div>
    </template>
  </VTooltip>
</template>

<style lang="scss" scoped>
// VTooltip のルート(.v-popper)は既定で display:block のため、放置すると
// 親幅いっぱいに広がり trigger 基準のツールチップ配置がズレる。
// trigger(バッジ)幅に収めて正しく配置させる。
.source-badge {
  display: inline-flex;
  flex-shrink: 0;
}

.document-card {
  width: 248px;
}
</style>
